import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const COMPRESSION_SOURCE = 'https://developer.mozilla.org/en-US/docs/Web/API/CompressionStream';

/**
 * CompressionStream round-trip fingerprint.
 *
 * The browser's DEFLATE implementation (zlib version, encoder settings) leaves
 * a byte-exact trace: compressing a known input and hashing the output is a
 * cheap fingerprint that needs no permissions and no network. The first byte
 * of a raw DEFLATE stream is a header byte whose BFINAL/BTYPE bits and the
 * resulting length differ subtly between zlib builds and browser versions.
 */
export const compressorProbe: Probe = {
  id: 'compressor',
  label: 'Compression',
  layer: 'engine',
  tier: 'client',
  category: 'browser',
  description: 'CompressionStream DEFLATE output fingerprint',
  sources: [COMPRESSION_SOURCE],
  budgetMs: 2000,

  async run(): Promise<ProbeResult> {
    if (typeof CompressionStream === 'undefined' || typeof Response === 'undefined') {
      return {
        signals: {
          supported: na<boolean>('unsupported', 'client', 'CompressionStream unavailable'),
        },
      };
    }

    // A deterministic input: repeated text plus a byte sweep, so the encoder's
    // match-finding behaviour is exercised.
    const text = 'The quick brown fox jumps over the lazy dog. '.repeat(20) +
      Array.from({ length: 256 }, (_, i) => String.fromCharCode(i)).join('');
    const input = new TextEncoder().encode(text);

    let deflate: { okBytes: number; hash: string; firstByte: number } | null = null;
    let gzipOk = false;

    try {
      const compressed = await compress(input, 'deflate-raw');
      if (compressed) {
        deflate = {
          okBytes: compressed.byteLength,
          hash: await fnv1aHex(bytesToHex(compressed)),
          firstByte: compressed[0] ?? 0,
        };
      }
    } catch {
      deflate = null;
    }

    try {
      const gz = await compress(input, 'gzip');
      gzipOk = !!gz && gz.byteLength > 0;
    } catch {
      gzipOk = false;
    }

    if (!deflate) {
      return {
        signals: {
          supported: na<boolean>('error', 'client', 'CompressionStream deflate-raw failed'),
        },
      };
    }

    return {
      signals: {
        supported: ok(true, 'client'),
        deflateRawLength: ok(deflate.okBytes, 'client'),
        deflateRawHash: ok(deflate.hash, 'client'),
        // BFINAL/BTYPE + window bits live in this byte; a stable differentiator.
        deflateFirstByte: ok(deflate.firstByte, 'client'),
        gzipSupported: ok(gzipOk, 'client'),
        // Compression ratio is itself stable per implementation.
        ratio: ok(Math.round((input.byteLength / Math.max(1, deflate.okBytes)) * 100) / 100, 'client'),
      },
      parts: {
        browserFeatures: {
          // Byte-exact output for a fixed input: a stable engine property.
          deflateHash: deflate.hash,
        },
      },
    };
  },
};

async function compress(
  data: Uint8Array,
  format: 'deflate-raw' | 'gzip' | 'deflate',
): Promise<Uint8Array | null> {
  try {
    const stream = new Blob([data as unknown as BlobPart]).stream()
      .pipeThrough(new CompressionStream(format));
    const buf = await new Response(stream).arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

async function fnv1aHex(input: string): Promise<string> {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 = (Math.imul(h2 ^ c, 0x85ebca6b) >>> 0) ^ (h2 >>> 13);
  }
  return (h1.toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0')).slice(0, 16);
}

registerProbe(compressorProbe);
